import { useState, useCallback, useRef } from "react";
import { ToolContainer, FileDropZone, ProcessingState, DownloadButton, downloadBlob, ResultPreview } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { compressImageToTarget, compressImageAtQuality, type ImageCompressionOutcome } from "./operations";
import { decodeImage, canvasToBlob, renamedTo } from "./decode";
import { Target } from "lucide-react";
import { DEFAULT_KEEP_DIMENSIONS, effectiveKeepDimensions, describeCompressResult, formatBytes } from "./compressImagePolicy";
import { BatchRunner, BatchModeToggle } from "@/components/BatchRunner";

const TOOL_INFO_CONTENT = {
  what: `Reduces an image's file size while keeping it looking as close to the original as possible, useful for speeding up a website, fitting an email attachment limit, or saving storage space. Medium/Strong aim for at least a 25%/50% smaller file instead of a vague quality label. "Custom Target" lets you set any exact size limit instead.`,
  how: `Upload your image, pick Medium or Strong for a percentage reduction, or switch to Custom Target and enter your own size limit. Compare the file size before and after, and use the quality slider if you want manual control instead. To shrink many pictures with the same setting, switch to Several files (up to 5 at a time on the trial, counted as one use).`,
  tip: `JPG images generally compress much further than PNGs before quality loss becomes noticeable — if you don't need transparency, converting a PNG to JPG first often gets you a smaller file overall.`,
};

type Mode = "medium" | "strong" | "quality" | "target";
// Guaranteed reduction targets -- computed as a percentage of the
// current file's size and run through the same proven target-search
// algorithm as the manual Target Size mode, so the result is exactly as
// reliable as that mode already was.
const PERCENT_TARGETS: Partial<Record<Mode, number>> = {
  medium: 0.75, // 25% reduction
  strong: 0.5, // 50% reduction
};
const TARGET_PRESETS = [
  { label: "50 KB", bytes: 50 * 1024 },
  { label: "100 KB", bytes: 100 * 1024 },
  { label: "200 KB", bytes: 200 * 1024 },
  { label: "500 KB", bytes: 500 * 1024 },
  { label: "1 MB", bytes: 1024 * 1024 },
];

/** Several pictures, one setting. A picture already under its target is returned as it was. */
function CompressImageBatch() {
  const [mode, setMode] = useState<Mode>("medium");
  const [quality, setQuality] = useState([75]);
  const [targetInput, setTargetInput] = useState("200");
  const [targetUnit, setTargetUnit] = useState<"KB" | "MB">("KB");
  const [keepDimensions, setKeepDimensions] = useState(DEFAULT_KEEP_DIMENSIONS);
  const targetBytes = () => Math.round(parseFloat(targetInput || "0") * (targetUnit === "MB" ? 1024 * 1024 : 1024));
  return (
    <BatchRunner
      accept="image/*" noun="image" zipName="compressed-images.zip" runLabel="Compress all"
      check={() => (mode === "target" && !(targetBytes() > 0) ? "Enter a valid target size" : null)}
      includeOriginals
      process={async (f, signal) => {
        const out = mode === "quality"
          ? await compressImageAtQuality(f, quality[0] / 100)
          : await compressImageToTarget(f, mode === "target" ? targetBytes() : Math.round(f.size * (PERCENT_TARGETS[mode] as number)), { keepDimensions: effectiveKeepDimensions(mode, keepDimensions), signal });
        if (out.alreadyUnder || out.keptOriginal || out.size >= f.size) return { blob: f, fileName: f.name };
        return { blob: out.blob, fileName: renamedTo(f.name, out.extension, "compressed_") };
      }}
    >
      <div className="space-y-3">
        <div className="flex gap-2 flex-wrap" role="group" aria-label="Compression mode">
          {(["medium", "strong", "quality", "target"] as Mode[]).map((m) => (
            <Button key={m} size="sm" variant={mode === m ? "default" : "outline"} aria-pressed={mode === m} onClick={() => setMode(m)} data-testid={`ci-mode-${m}`}>
              {m === "medium" ? "Medium (25% smaller)" : m === "strong" ? "Strong (50% smaller)" : m === "quality" ? "Manual quality" : "Custom target"}
            </Button>
          ))}
        </div>
        {mode === "quality" && <div className="space-y-2 max-w-xs"><Label>Quality: {quality[0]}%</Label><Slider value={quality} onValueChange={setQuality} min={10} max={100} step={5} /></div>}
        {mode === "target" && (
          <div className="flex gap-2 items-end">
            <div><Label className="text-xs">Target size for each picture</Label>
              <div className="flex gap-2">
                <Input type="number" min="0" step="1" value={targetInput} onChange={(e) => setTargetInput(e.target.value)} className="w-24" data-testid="ci-target" />
                <select value={targetUnit} aria-label="Target size unit" onChange={(e) => setTargetUnit(e.target.value as "KB" | "MB")} className="border rounded-md px-2 text-sm bg-background"><option value="KB">KB</option><option value="MB">MB</option></select>
              </div></div>
          </div>
        )}
        {mode === "target" && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={keepDimensions} onChange={(e) => setKeepDimensions(e.target.checked)} data-testid="ci-keep-dimensions" /> Don't change dimensions (the size limit may then be missed)</label>}
        <p className="text-xs text-muted-foreground">{mode === "target" ? "Quality is lowered first; a picture is made smaller in pixels only if quality alone cannot reach the limit. " : "Medium, Strong and Manual quality never change pixel size. "}Pictures that cannot be made smaller, and SVG files (already small vector art), are returned as they were. Stop ends the current picture at once and keeps the finished ones. Photos are saved as JPG; pictures with transparency as WebP. Metadata such as location is removed from every picture that is compressed.</p>
      </div>
    </BatchRunner>
  );
}

export default function CompressImage() {
  const [batch, setBatch] = useState(false);
  const header = <BatchModeToggle batch={batch} onChange={setBatch} />;
  if (batch) return <ToolContainer>{header}<CompressImageBatch /><ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} /></ToolContainer>;
  return <CompressImageSingle header={header} />;
}

function CompressImageSingle({ header }: { header: React.ReactNode }) {
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<Mode>("medium");
  const [quality, setQuality] = useState([75]);
  const [targetInput, setTargetInput] = useState("200");
  const [targetUnit, setTargetUnit] = useState<"KB" | "MB">("KB");
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<ImageCompressionOutcome | null>(null);
  const [keepDimensions, setKeepDimensions] = useState(DEFAULT_KEEP_DIMENSIONS);
  const [progress, setProgress] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const [preview, setPreview] = useState<string>("");

  const handleFiles = useCallback((files: File[]) => {
    const f = files[0]; if (!f) return;
    setResult(null);
    // decodeImage opens HEIC / AVIF / TIFF too; the preview is always a plain PNG the page can show.
    decodeImage(f).then(async ({ canvas, width, height }) => {
      setFile(f); setDims({ w: width, h: height });
      setPreview(URL.createObjectURL(await canvasToBlob(canvas, "image/png")));
    }).catch((e) => toast.error(e?.message || "This file could not be opened as an image"));
  }, []);
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);

  const stop = () => abortRef.current?.abort();

  const compress = async () => {
    if (!file) return;
    setProcessing(true); setProgress(0);
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      let outcome: ImageCompressionOutcome;
      if (mode === "quality") {
        outcome = await compressImageAtQuality(file, quality[0] / 100);
      } else if (mode === "medium" || mode === "strong") {
        const targetBytes = Math.round(file.size * (PERCENT_TARGETS[mode] as number));
        outcome = await compressImageToTarget(file, targetBytes, { keepDimensions: effectiveKeepDimensions(mode, keepDimensions), onProgress: setProgress, signal: ac.signal });
      } else {
        const targetBytes = Math.round(parseFloat(targetInput || "0") * (targetUnit === "MB" ? 1024 * 1024 : 1024));
        if (!targetBytes || targetBytes <= 0) {
          toast.error("Enter a valid target size");
          setProcessing(false);
          return;
        }
        outcome = await compressImageToTarget(file, targetBytes, { keepDimensions: effectiveKeepDimensions(mode, keepDimensions), onProgress: setProgress, signal: ac.signal });
      }
      setResult(outcome);
      if (outcome.keptOriginal) toast.info(outcome.keptOriginal === "vector" ? "SVG is already a small vector file - returned unchanged" : "Couldn't make it smaller - the original is returned");
      else if (outcome.alreadyUnder) toast.info("Already under your target - no compression needed");
      else if (mode === "quality") toast.success(`Compressed! Reduced by ${((1 - outcome.size / file.size) * 100).toFixed(1)}%`);
      else if (outcome.metTarget && outcome.resized) toast.success(`Reached your target by resizing to ${outcome.width} x ${outcome.height} px`);
      else if (outcome.metTarget) toast.success("Compressed to your target");
      else toast.error("Couldn't fully hit the target - here's the smallest we could achieve");
    } catch (e) {
      if ((e as Error)?.name === "AbortError") toast.info("Stopped");
      else toast.error((e as Error)?.message || "Failed to compress image");
    }
    finally { setProcessing(false); }
  };

  const fmtBytes = formatBytes;

  return (
    <ToolContainer>
      {header}
      {!file ? (
        <FileDropZone accept="image/*" onFiles={handleFiles} label="Drop an image to compress" description="JPG, PNG, WebP, HEIC (iPhone), AVIF, TIFF" gateAtDownload />
      ) : (
        <div className="space-y-4">
          {preview && <img src={preview} alt="Preview" className="max-h-64 mx-auto rounded-lg border" />}
          <div className="p-4 rounded-lg border bg-card flex justify-between">
            <div><p className="text-sm font-medium">{file.name}</p><p className="text-xs text-muted-foreground">Original: {(file.size/1024).toFixed(0)} KB</p></div>
            {result && <p className="text-sm font-medium text-green-600">{(result.size/1024).toFixed(result.size < 10240 ? 1 : 0)} KB</p>}
          </div>

          <p className="text-xs text-muted-foreground -mt-2">
            Photos are saved as JPG. Pictures with a transparent background are saved as WebP so the transparency is kept.
            For a Custom Target, quality is lowered first and the picture is made smaller in pixels only if quality alone
            cannot reach your size limit. Medium, Strong and Manual Quality never change pixel size. Metadata such as location is removed.
          </p>
          {mode === "target" && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={keepDimensions} onChange={(e) => { setKeepDimensions(e.target.checked); setResult(null); }} data-testid="ci-keep-dimensions" />
              Don't change dimensions{dims ? ` (${dims.w} x ${dims.h} px)` : ""} - the size limit may then be missed
            </label>
          )}

          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant={mode === "medium" ? "default" : "outline"} onClick={() => { setMode("medium"); setResult(null); }}>Medium (25% smaller)</Button>
            <Button size="sm" variant={mode === "strong" ? "default" : "outline"} onClick={() => { setMode("strong"); setResult(null); }}>Strong (50% smaller)</Button>
            <Button size="sm" variant={mode === "quality" ? "default" : "outline"} onClick={() => { setMode("quality"); setResult(null); }}>Manual Quality</Button>
            <Button size="sm" variant={mode === "target" ? "default" : "outline"} onClick={() => { setMode("target"); setResult(null); }} className="gap-1.5">
              <Target className="h-3.5 w-3.5" /> Custom Target
            </Button>
          </div>

          {mode === "quality" ? (
            <div className="space-y-2">
              <Label>Quality: {quality[0]}%</Label>
              <Slider value={quality} onValueChange={setQuality} min={10} max={100} step={5} />
            </div>
          ) : mode === "medium" || mode === "strong" ? (
            <p className="text-xs text-muted-foreground">
              Targets a real {mode === "medium" ? "25%" : "50%"} smaller file than the original — click Compress below.
            </p>
          ) : (
            <div className="space-y-2">
              <div className="flex items-end gap-2">
                <div>
                  <Label className="text-xs">Target size</Label>
                  <div className="flex gap-2">
                    <Input type="number" min="0" step="1" value={targetInput} onChange={(e) => setTargetInput(e.target.value)} className="w-24" />
                    <select
                      value={targetUnit}
                      onChange={(e) => setTargetUnit(e.target.value as "KB" | "MB")}
                      className="border rounded-md px-2 text-sm bg-background"
                    >
                      <option value="KB">KB</option>
                      <option value="MB">MB</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-1.5 flex-wrap pb-0.5">
                  {TARGET_PRESETS.map((p) => (
                    <Button
                      key={p.label}
                      size="sm"
                      variant="ghost"
                      className="h-8 text-xs"
                      onClick={() => {
                        setTargetInput(String(p.bytes / (p.bytes >= 1024 * 1024 ? 1024 * 1024 : 1024)));
                        setTargetUnit(p.bytes >= 1024 * 1024 ? "MB" : "KB");
                      }}
                    >
                      {p.label}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {result && (
            <p className={`text-xs font-medium ${result.metTarget ? "text-green-600" : "text-amber-600"}`} data-testid="compress-result">
              {result.keptOriginal === "vector"
                ? "An SVG is already a small vector file, so it is returned unchanged. Turning it into a JPG would make it bigger and lose the sharp edges."
                : result.keptOriginal === "not-smaller"
                ? "No setting could make this picture smaller than the original, so the original is returned unchanged."
                : result.alreadyUnder
                ? `Your image is already under the target (${fmtBytes(file.size)}) - no compression needed. The original is returned unchanged.`
                : describeCompressResult(result, mode, keepDimensions)}
            </p>
          )}

          {result && <ResultPreview blob={result.blob} />}

          <div className="flex gap-3">
            {processing ? <ProcessingState message="Compressing..." progress={progress * 100} onStop={stop} /> : result ? (
              <><DownloadButton blob={result.blob} freeDownload={!!(result.alreadyUnder || result.keptOriginal)} fileName={result.alreadyUnder || result.keptOriginal ? file.name : renamedTo(file.name, result.extension, "compressed_")} /><Button variant="outline" onClick={() => { setFile(null); setResult(null); }}>New</Button></>
            ) : (
              <><Button onClick={compress}>Compress</Button><Button variant="outline" onClick={() => setFile(null)}>Reset</Button></>
            )}
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
